import React from 'react';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useAuth } from '../../auth';
import { exchangeExternalRef, redirectToAzureLogin } from '../../auth/api';
import { useGlobalError } from '../../common/ErrorHandler';

const LOGIN_REDIRECT_ATTEMPT_KEY = 'rfi.auth.loginRedirectAttemptAt';
const LOGIN_REDIRECT_ATTEMPT_TTL_MS = 60_000;
const inFlightExternalRefExchanges = new Map<string, ReturnType<typeof exchangeExternalRef>>();

function getOrStartExternalRefExchange(ref: string) {
  const existing = inFlightExternalRefExchanges.get(ref);
  if (existing) return existing;

  const exchange = exchangeExternalRef(ref).finally(() => {
    inFlightExternalRefExchanges.delete(ref);
  });
  inFlightExternalRefExchanges.set(ref, exchange);
  return exchange;
}

export const ProtectedShell = () => {
  const { hydrated, session, login } = useAuth();
  const { setError: setGlobalError } = useGlobalError();
  const loginRedirectStartedRef = React.useRef(false);
  // Tracks whether the hard-timeout fired — prevents the automatic Azure redirect
  // that would otherwise re-authenticate the user silently when session becomes null.
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [isBootstrappingExternalRef, setIsBootstrappingExternalRef] = React.useState(false);
  const [authFailed, setAuthFailed] = React.useState(false);

  const getRefFromUrl = React.useCallback(() => {
    if (typeof window === 'undefined') return null;
    const ref = new URLSearchParams(window.location.search).get('REF')?.trim() ?? '';
    if (!ref) return null;
    return /^[A-Za-z0-9_-]{1,64}$/.test(ref) ? ref : null;
  }, []);

  const hasRecentRedirectAttempt = React.useCallback(() => {
    if (typeof window === 'undefined') return false;

    const raw = window.sessionStorage.getItem(LOGIN_REDIRECT_ATTEMPT_KEY);
    if (!raw) return false;

    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) {
      window.sessionStorage.removeItem(LOGIN_REDIRECT_ATTEMPT_KEY);
      return false;
    }

    if (Date.now() - timestamp > LOGIN_REDIRECT_ATTEMPT_TTL_MS) {
      window.sessionStorage.removeItem(LOGIN_REDIRECT_ATTEMPT_KEY);
      return false;
    }

    return true;
  }, []);

  const clearRedirectAttempt = React.useCallback(() => {
    loginRedirectStartedRef.current = false;
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(LOGIN_REDIRECT_ATTEMPT_KEY);
    }
  }, []);

  const redirectToAzureLoginOnce = React.useCallback(() => {
    if (loginRedirectStartedRef.current || hasRecentRedirectAttempt()) return;
    loginRedirectStartedRef.current = true;
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(LOGIN_REDIRECT_ATTEMPT_KEY, String(Date.now()));
    }
    redirectToAzureLogin();
  }, [hasRecentRedirectAttempt]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = () => {
      return setSessionExpired(true);
    };
    window.addEventListener('rfi:session:force-logout', handle);
    return () => {
      return window.removeEventListener('rfi:session:force-logout', handle);
    };
  }, []);

  React.useEffect(() => {
    if (!hydrated || !session || typeof window === 'undefined') return;

    // Session is active; future unauthenticated transitions may redirect again.
    clearRedirectAttempt();

    if (window.location.pathname === '/') {
      window.history.replaceState({}, document.title, '/home');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [hydrated, session, clearRedirectAttempt]);

  React.useEffect(() => {
    if (!hydrated || session || typeof window === 'undefined') return;
    if (window.location.pathname === '/signed-out' || sessionExpired || authFailed) return;

    const ref = getRefFromUrl();
    if (!ref) {
      redirectToAzureLoginOnce();
      return;
    }

    let active = true;
    setIsBootstrappingExternalRef(true);

    void (async () => {
      try {
        const nextSession = await getOrStartExternalRefExchange(ref);
        if (!active) return;
        login(nextSession);
        window.history.replaceState({}, document.title, '/home');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch {
        if (!active) return;
        setAuthFailed(true);
        setGlobalError({
          title: 'External Sign-in Failed',
          message: 'Authentication failed. Close this tab and open the application in a new tab.',
          isAuthError: true,
          reason: 'exchange-failure',
        });
      } finally {
        if (active) {
          setIsBootstrappingExternalRef(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [
    authFailed,
    getRefFromUrl,
    hydrated,
    login,
    redirectToAzureLoginOnce,
    session,
    sessionExpired,
    setGlobalError,
  ]);

  if (!hydrated) {
    return (
      <Layout.Stack alignLayout="center" css={{ minHeight: '80vh' }}>
        <Text>Loading session...</Text>
      </Layout.Stack>
    );
  }

  // The app-wide global dialog handles the hard-timeout action.
  if (sessionExpired) {
    return null;
  }

  if (!session) {
    if (authFailed) return null;

    if (isBootstrappingExternalRef) {
      return (
        <Layout.Stack alignLayout="center" css={{ minHeight: '80vh' }}>
          <Text>Completing secure sign-in...</Text>
        </Layout.Stack>
      );
    }

    return null;
  }

  return (
    <Router.Outlet />
  );
};
